import { ATBeaconButton } from 'components/common/beacon/beaconRegistration';
import CircleGroup from 'components/common/icons/CircleGroup';
import CircleOne from 'components/common/icons/CircleOne';
import CircleThree from 'components/common/icons/CircleThree';
import CircleTwo from 'components/common/icons/CircleTwo';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLazyGetTransferConfirmationDownloadQuery } from 'src/api/atv/atvApi';
import IconList from 'src/components/common/IconList/IconList';
import { FooterSlot } from 'src/features/transfer/components/StepFooter/StepFooter';
import { RootState } from 'src/state/store';
import { dispatchPageLoad } from 'utils/analyticsUtil';
import { CTAVariants } from '@trp-beacon-ui-kit/foundation/dist/_common/types/cta-variant';
import '../DocusignReturn.scss';

interface DocusignSignAndReturnProps {
  confirmationNumber: string;
  accountType: string;
  institutionName: string;
}

const AddressSection: React.FC<{ title: string; addressLines: string[] }> = ({
  title,
  addressLines,
}) => (
  <div className="address-section">
    <div className="b-font-weight-bold">{title}</div>
    {addressLines.map((line, index) => (
      <div key={index}>{line}</div>
    ))}
  </div>
);

const DocusignSignAndReturn: React.FC<DocusignSignAndReturnProps> = ({
  confirmationNumber,
  accountType,
  institutionName,
}) => {
  const cancelUrl = useSelector((state: RootState) => state.appConfig.cancelUrl);
  const [getTransferConfirmationDownload] = useLazyGetTransferConfirmationDownloadQuery();

  const downloadDocuSignForm = async () => {
    try {
      await getTransferConfirmationDownload(confirmationNumber);
    } catch (error) {
      console.error('Error downloading the file:', error);
    }
  };

  const documentSteps = [
    {
      message: (
        <>
          <span className="b-font-weight-bold">
            Download your DocuSign transfer form.
          </span>
          <div className="document-step-details">
            <div>
              <ATBeaconButton
                className="button-docusign-download"
                variant={CTAVariants.Primary}
                onClick={(e) => {
                  e.preventDefault(); // Prevent default link behavior
                  downloadDocuSignForm(); // Call the function
                }}
              >
                Download DocuSign Form
              </ATBeaconButton>
            </div>
          </div>
        </>
      ),
      Icon: CircleOne,
    },
    {
      message: (
        <span className="b-font-weight-bold">
          Print your latest {`${accountType} statement from ${institutionName}`}.
        </span>
      ),
      Icon: CircleTwo,
    },
    {
      message: (
        <>
          <span className="b-font-weight-bold">
            Mail all signed documents and statements to:
          </span>
          <AddressSection
            title="Mail to:"
            addressLines={['T. Rowe Price', 'P.O. Box 17300', 'Baltimore, MD 21297-1300']}
          />
          <AddressSection
            title="Express delivery only:"
            addressLines={[
              'T. Rowe Price',
              'Mail Code 17150',
              '4515 Painters Mill Road',
              'Owing Mills, MD 21117-4903',
            ]}
          />
        </>
      ),
      Icon: CircleThree,
    },
  ];

  const nextSteps = [
    {
      message: (
        <>
          Once we receive all the required paperwork, it may take up to 7-10 business days
          to process your transfer. We will reach out to you if any additional documents
          are needed.
        </>
      ),
      Icon: CircleGroup,
    },
  ];
  useEffect(() => {
    dispatchPageLoad('ConfirmationSignAndMail');
  }, []);
  return (
    <main>
      <section className="layout-section">
        <div className="b-typography-serif--headline--500">
          Sign and mail your transfer form
        </div>
        <div className="content-container b-grid --b-grid-gap=200">
          <div className="content-left b-grid-item b-xs:12-col b-sm:12-col b-md:8-col b-lg:8-col">
            <IconList
              title="T. Rowe Price can initiate your transfer once we've received some critical documents."
              data={documentSteps}
            />
            <IconList title="What's next?" data={nextSteps} />
          </div>
        </div>
      </section>
      <div className="docusign-footer">
        <div className="footer-right">
          <ATBeaconButton
            variant={CTAVariants.Primary}
            slot={FooterSlot.Right}
            onClick={() => window.location.assign(cancelUrl)}
          >
            Return to Transactions
          </ATBeaconButton>
        </div>
      </div>
    </main>
  );
};

export default DocusignSignAndReturn;
